import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, PlugZap, RefreshCw, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loading } from "@/components/Common";
import { SimpleSelect } from "@/components/Filters";
import { useLoad } from "@/hooks/useData";
import { api, errMsg } from "@/lib/api";

const FIELDS = {
  delhivery: (f) => [
    ["mode", "API type", "select", [["ltl", "B2B / LTL (username + password)"], ["token", "Express API token"]]],
    ...(f.mode === "token" ? [["api_token", "API token", "secret"]] : [["username", "Username", "text"], ["password", "Password", "secret"],
      ["track_path", "Tracking path ({lrn} = LRN)", "text"]]),
  ],
  bluedart: () => [["client_id", "Client ID (consumer key)", "text"], ["client_secret", "Client secret", "secret"],
    ["login_id", "Login ID", "text"], ["licence_key", "Licence key", "secret"]],
};
const TITLE = { delhivery: "Delhivery B2B / cargo (LRN)", bluedart: "Blue Dart (AWB)" };

const Field = ({ label, children }) => <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;

const TestBox = ({ courier }) => {
  const [tid, setTid] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const run = async () => {
    setBusy(true);
    try { setRes((await api.post(`/shipments/couriers/${courier}/test`, { tracking_id: tid })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2 rounded-md border border-border bg-slate-50 p-3">
      <div className="flex gap-2">
        <Input value={tid} onChange={(e) => setTid(e.target.value)} placeholder="Tracking ID / LRN to test" className="font-mono" data-testid={`${courier}-test-input`} />
        <Button variant="outline" disabled={!tid || busy} onClick={run} data-testid={`${courier}-test-btn`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlugZap className="h-4 w-4" />} Test connection</Button>
      </div>
      {res && (
        <div className="text-xs" data-testid={`${courier}-test-result`}>
          <div className={res.ok ? "font-semibold text-emerald-700" : "font-semibold text-red-700"}>{res.ok ? `Connected · ${res.parsed.length} status found` : res.error}</div>
          {res.parsed.map((p) => <div key={p.t} className="font-mono">{p.t}: {p.st} {p.ex ? `(expected ${p.ex})` : ""}</div>)}
          {res.raw && <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10px]">{typeof res.raw === "string" ? res.raw : JSON.stringify(res.raw, null, 2)}</pre>}
        </div>
      )}
    </div>
  );
};

const CourierCard = ({ courier, cfg, onSaved }) => {
  const [f, setF] = useState(cfg);
  useEffect(() => setF(cfg), [cfg]);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e?.target ? e.target.value : e }));
  const save = async () => {
    try { onSaved(courier, (await api.put(`/shipments/couriers/${courier}/settings`, { ...f, days: Number(f.days) || 60 })).data); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const sync = async () => { try { toast.success((await api.post(`/shipments/couriers/${courier}/sync`)).data.message); } catch (e) { toast.error(errMsg(e)); } };
  const rotate = async () => {
    if (!window.confirm("Create a new webhook link? The old link stops working.")) return;
    try { onSaved(courier, (await api.post(`/shipments/couriers/${courier}/webhook-token`)).data); toast.success("New webhook link created"); } catch (e) { toast.error(errMsg(e)); }
  };
  const hook = cfg.webhook_path ? `${process.env.REACT_APP_BACKEND_URL}${cfg.webhook_path}` : null;
  return (
    <div className="panel space-y-4 p-4 sm:p-5" data-testid={`${courier}-card`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-base font-bold">{TITLE[courier]}</div>
        <label className="flex items-center gap-2 text-sm font-medium">Hourly sync <Switch checked={!!f.enabled} onCheckedChange={set("enabled")} data-testid={`${courier}-enabled`} /></label>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Environment"><SimpleSelect value={f.environment} onChange={set("environment")} testid={`${courier}-env`} className="h-9 w-full" options={[["production", "Production"], ["staging", "Staging / sandbox"]]} /></Field>
        <Field label="Track shipments ordered in last (days)"><Input type="number" min="1" max="180" value={f.days} onChange={set("days")} data-testid={`${courier}-days`} /></Field>
        {FIELDS[courier](f).map(([k, label, type, opts]) => (
          <Field key={k} label={label}>
            {type === "select" ? <SimpleSelect value={f[k]} onChange={set(k)} testid={`${courier}-${k}`} className="h-9 w-full" options={opts} />
              : <Input type={type === "secret" ? "password" : "text"} autoComplete="new-password" value={f[k] || ""} onChange={set(k)}
                placeholder={type === "secret" && cfg[`${k}_saved`] ? "saved •••• (type to replace)" : ""} data-testid={`${courier}-${k}`} />}
          </Field>
        ))}
        <Field label="Base URL (leave empty for default)"><Input value={f.base_url || ""} onChange={set("base_url")} placeholder={cfg.default_base_url} data-testid={`${courier}-base-url`} /></Field>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={save} className="bg-[#071434] hover:bg-[#0d1f4d]" data-testid={`${courier}-save-btn`}><Save className="h-4 w-4" /> Save</Button>
        <Button variant="outline" onClick={sync} className="border-amber-400 text-amber-800" data-testid={`${courier}-sync-btn`}><RefreshCw className="h-4 w-4" /> Sync now</Button>
      </div>
      <TestBox courier={courier} />
      <div className="space-y-1 text-xs">
        <div className="font-semibold">Webhook link (give this to the courier for push updates)</div>
        {hook ? (
          <div className="flex flex-wrap items-center gap-2">
            <code className="break-all rounded bg-slate-100 px-2 py-1" data-testid={`${courier}-webhook-url`}>{hook}</code>
            <Button size="sm" variant="outline" onClick={() => navigator.clipboard.writeText(hook).then(() => toast.success("Copied"))}><Copy className="h-3.5 w-3.5" /> Copy</Button>
            <Button size="sm" variant="ghost" onClick={rotate} data-testid={`${courier}-rotate-webhook`}>New link</Button>
          </div>
        ) : <div className="text-muted-foreground">Save the settings once to create the link.</div>}
      </div>
    </div>
  );
};

export default function CourierSettings() {
  const { data, setData } = useLoad(() => api.get("/shipments/couriers/settings").then((r) => r.data));
  if (!data) return <Loading />;
  const onSaved = (c, cfg) => setData((d) => ({ ...d, [c]: cfg }));
  return (
    <div className="max-w-5xl">
      <PageHeader title="Courier APIs" subtitle="Hourly tracking updates for open shipments. Passwords and keys are stored encrypted and never shown again. Closed shipments are never changed." />
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {Object.keys(data).map((c) => <CourierCard key={c} courier={c} cfg={data[c]} onSaved={onSaved} />)}
      </div>
    </div>
  );
}
